import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { API } from "../services/api";
import ReplyMailModal from "./ReplyMailModal";
import OrdonnanceModal from "./ordonnance/OrdonnanceModal";
import MailMessageContent from "./MailMessageContent";
import { isFaxSender, isPharmacyAuthoredMessage, isPortalConversation } from "../utils/commandeMail";
import { navigateToValidationPharma } from "../utils/navigation";
import "./CommandeDetailModal.css";

function formatDateTime(dateStr) {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

function incomingSenderLabel(commande) {
    if (commande.source === "PORTAIL_COLLECTIVITE") return "Portail collectivité";
    return commande.emailEmetteur || "EHPAD";
}

export default function CommandeDetailModal({ commandeId, onClose, onBeforeOpenValidation }) {
    const [data, setData] = useState(null);
    const [showReply, setShowReply] = useState(false);
    const [selectedOrdonnance, setSelectedOrdonnance] = useState(null);
    const closeButtonRef = useRef(null);

    useEffect(() => {
        API.get(`/commandes/${commandeId}`).then(r => setData(r.data)).catch(console.error);
    }, [commandeId]);

    useEffect(() => {
        closeButtonRef.current?.focus();
    }, []);

    const files = data
        ? (data.fileNames || []).map((name, i) => ({ name, id: (data.ordonnanceIds || [])[i] ?? null }))
        : [];
    const messages = data?.messages || [];
    const portalConversation = isPortalConversation(data);
    const replyDisabled = !portalConversation && (!data?.emailEmetteur || isFaxSender(data));

    const openValidationDetail = async (event) => {
        event.preventDefault();
        await onBeforeOpenValidation?.();
        onClose?.();
        navigateToValidationPharma(commandeId);
    };

    const handleKeyDown = (event) => {
        if (event.key === "Escape" && !showReply && !selectedOrdonnance) onClose?.();
    };

    // Rendu dans <body> : la fenêtre reste centrée même ouverte depuis un tableau qui crée son propre contexte d'empilement.
    return createPortal(
        <div className="commande-mail-modal">
            <button type="button" className="commande-mail-modal__backdrop" aria-label="Fermer la fenêtre du mail" onClick={onClose} />
            <dialog
                open
                className="commande-mail-modal__dialog"
                aria-labelledby="commande-mail-modal-title"
                onKeyDown={handleKeyDown}
            >
                <header className="commande-mail-modal__header">
                    <div className="commande-mail-modal__heading">
                        <span className="commande-mail-modal__eyebrow">Mail · commande #{commandeId}</span>
                        <h2 id="commande-mail-modal-title">{data?.commentaire || "(sans objet)"}</h2>
                    </div>
                    <button
                        ref={closeButtonRef}
                        type="button"
                        className="commande-mail-modal__close"
                        aria-label="Fermer le mail"
                        onClick={onClose}
                    >
                        &#215;
                    </button>
                </header>

                {data ? (
                    <div className="commande-mail-modal__body">
                        <dl className="commande-preview-metadata commande-mail-modal__metadata">
                            <div>
                                <dt>Reçu</dt>
                                <dd>{formatDateTime(data.dateReception) || "Date inconnue"}</dd>
                            </div>
                            <div>
                                <dt>Expéditeur</dt>
                                <dd>{data.emailEmetteur || "Non renseigné"}</dd>
                            </div>
                        </dl>

                        <div className="commande-mail-modal__columns">
                            <section className="commande-mail-modal__thread" aria-label="Échanges">
                                <div className="commande-preview-section-heading">
                                    <h3>Échanges</h3>
                                    <div className="commande-preview-thread-actions">
                                        <span>{messages.length} message{messages.length > 1 ? "s" : ""}</span>
                                        <button
                                            type="button"
                                            onClick={() => setShowReply(true)}
                                            disabled={replyDisabled}
                                            title={replyDisabled ? "Réponse indisponible pour cet expéditeur" : (portalConversation ? "Répondre dans le portail EHPAD" : "Répondre au mail")}
                                        >
                                            ↩ {portalConversation ? "Répondre sur le portail" : "Répondre"}
                                        </button>
                                    </div>
                                </div>
                                {messages.length === 0 ? (
                                    <p className="commande-mail-modal__muted">Aucun message.</p>
                                ) : (
                                    <div className="commande-preview-messages commande-mail-modal__messages">
                                        {messages.map((msg, i) => {
                                            const isReply = isPharmacyAuthoredMessage(msg);
                                            return (
                                                <article
                                                    key={msg.id ?? i}
                                                    className={`commande-preview-message${isReply ? " commande-preview-message--reply" : ""}`}
                                                >
                                                    <div className="commande-preview-message-meta">
                                                        <strong>{isReply ? "Vous" : incomingSenderLabel(data)}</strong>
                                                        <span>{formatDateTime(msg.dateMessage)}</span>
                                                    </div>
                                                    <div className={`commande-preview-message-body${msg.contenuHtml ? "" : " commande-mail-modal__plain-text"}`}>
                                                        {msg.sujet && isReply && (
                                                            <strong className="commande-preview-message-subject">{msg.sujet}</strong>
                                                        )}
                                                        <MailMessageContent html={msg.contenuHtml} text={msg.contenu} />
                                                    </div>
                                                </article>
                                            );
                                        })}
                                    </div>
                                )}
                            </section>

                            <section className="commande-mail-modal__attachments" aria-label="Pièces jointes">
                                <div className="commande-preview-section-heading">
                                    <h3>Pièces jointes</h3>
                                    <span>{files.length}</span>
                                </div>
                                {files.length === 0 ? (
                                    <p className="commande-mail-modal__muted">Aucune pièce jointe.</p>
                                ) : (
                                    <div className="commande-preview-attachment-list">
                                        {files.map((f, i) => (
                                            <button
                                                type="button"
                                                key={f.id ?? `${f.name}-${i}`}
                                                onClick={() => setSelectedOrdonnance(f)}
                                                title={f.name || "Fichier"}
                                            >
                                                <span aria-hidden="true">📎</span>
                                                <strong>{f.name || "Fichier"}</strong>
                                                <span aria-hidden="true">Ouvrir</span>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </section>
                        </div>
                    </div>
                ) : (
                    <div className="commande-mail-modal__loading">Chargement du mail…</div>
                )}

                <footer className="commande-mail-modal__footer">
                    <button type="button" className="commande-mail-modal__secondary" onClick={onClose}>Fermer</button>
                    <a
                        className="commande-mail-modal__primary"
                        href={`/validation-pharma?commandeId=${commandeId}`}
                        onClick={openValidationDetail}
                    >
                        Ouvrir en validation pharma
                    </a>
                </footer>
            </dialog>

            {showReply && (
                <ReplyMailModal commande={data} onClose={() => setShowReply(false)} />
            )}

            {selectedOrdonnance && (
                <OrdonnanceModal
                    ordonnance={selectedOrdonnance}
                    draft={null}
                    setDraft={() => {}}
                    onClose={() => setSelectedOrdonnance(null)}
                />
            )}
        </div>,
        document.body
    );
}
